import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {Event} from '~types/api/event';
import {TABS, ORDER_BY, ORDER_DIR, EVENT_AGE_GROUP} from '~types/events';
import {useAllEvents} from './useAllEvents';
import {useTranslation} from 'react-i18next';

interface UseListViewEventsProps {
  initialRadius: number;
  isForKids?: boolean;
  selectedTimeframe?: any;
  globalInputValue?: string;
  isNeighbourhood?: boolean;
  isPyxiSelect?: boolean;
  isPyxiCore?: boolean;
  userLocation?: {latitude: number; longitude: number} | null;
  enabled?: boolean;
  eventServiceType?: 'THE_REST_TABLE' | 'CULTURAL_EDIT' | 'GENERAL' | null;
}

/**
 * List view events hook with dynamic radius expansion
 * Expands radius as user scrolls to load more events
 */
export const useListViewEvents = ({
  initialRadius,
  isForKids,
  selectedTimeframe,
  globalInputValue,
  isNeighbourhood,
  isPyxiSelect,
  isPyxiCore,
  userLocation,
  enabled = true,
  eventServiceType,
}: UseListViewEventsProps) => {
  const [currentRadius, setCurrentRadius] = useState(initialRadius);
  const [isExpanding, setIsExpanding] = useState(false);
  const lastExpandRef = useRef<number>(Date.now());
  const maxRadius = useRef(1500); // Maximum radius of 1500km
  const {t} = useTranslation(undefined, { lng: 'en' });

  console.log('udhufhurfhuhrf',  t('home.upcoming_events'));
  
  /**
   * Fetch events from API with current radius
   */
  const {
    data: apiData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    remove,
    error,
  } = useAllEvents({
    distance_km: isNeighbourhood || isPyxiSelect ? 1000000000000 : currentRadius,
    tab: isNeighbourhood 
      ? TABS.NEIGHBOURHOOD_EVENT 
     /* : isPyxiSelect 
        ? TABS.PYXI_SELECT_EVENT */
        : TABS.DISCOVER,
    order_by: ORDER_BY.START_DATE,
    order_dir: ORDER_DIR.ASC,
    event_age_group: isForKids ? EVENT_AGE_GROUP.CHILDREN : undefined,
    filter_type:
      selectedTimeframe?.value === t('home.upcoming_events') ||
      isNeighbourhood ||
      isPyxiSelect ||
      isPyxiCore
        ? undefined
        : selectedTimeframe?.value,
    q: globalInputValue,
    isNeighbourhood: isNeighbourhood,
    enabled: enabled && !!userLocation, // Only fetch when user location is available
    timeframe:
      selectedTimeframe?.value === t('home.upcoming_events')
        ? 'future'
        : selectedTimeframe?.value === t('events.past')
          ? 'past'
          : selectedTimeframe.title === t('events.pyxi_core') ? 'future' : 'future',
    event_service_type: eventServiceType || (isPyxiCore ? 'GENERAL' : undefined) // Use passed eventServiceType or fallback to GENERAL for PyXi Core
  });
  /**
   * Expand radius to load more events
   */
  const expandRadius = useCallback(() => {
    const now = Date.now();
    const timeSinceLastExpand = now - lastExpandRef.current;

    // Prevent too frequent expansions
    if (timeSinceLastExpand < 2000 || isExpanding) {
      console.log(`⏳ [LIST VIEW] Skipping radius expansion (too soon or already expanding)`);
      return;
    }

    // Don't expand beyond maximum radius
    if (currentRadius >= maxRadius.current) {
      console.log(`🚫 [LIST VIEW] Maximum radius reached (${maxRadius.current}km)`);
      return;
    }

    setIsExpanding(true);
    lastExpandRef.current = now;

    // More conservative expansion to avoid API limits
    let expansion;
    if (currentRadius <= 50) {
      expansion = 10; // 10km increments for small radius
    } else if (currentRadius <= 100) {
      expansion = 20; // 20km increments for medium radius
    } else if (currentRadius <= 200) {
      expansion = 30; // 30km increments for large radius
    } else if (currentRadius <= 500) {
      expansion = 50; // 50km increments for very large radius
    } else {
      expansion = 100; // 100km increments for extreme radius
    }

    const newRadius = Math.round(Math.min(currentRadius + expansion, maxRadius.current));

    console.log(`📏 [LIST VIEW] Expanding radius from ${currentRadius}km to ${newRadius}km (rounded to integer)`);
    setCurrentRadius(newRadius);

    // Reset expanding state after a delay
    setTimeout(() => {
      setIsExpanding(false);
    }, 1000);
  }, [currentRadius, isExpanding]);

  /**
   * Reset radius to initial value
   */
  const resetRadius = useCallback(() => {
    console.log(`🔄 [LIST VIEW] Resetting radius to ${initialRadius}km`);
    setCurrentRadius(initialRadius);
    setIsExpanding(false);
    lastExpandRef.current = Date.now();
  }, [initialRadius]);

  // Calculate event count from API data
  const eventCount = useMemo(() => {
    if (!apiData?.pages) return 0;
    return apiData.pages.reduce((total, page) => {
      if (page?.items && Array.isArray(page.items)) {
        return total + page.items.length;
      } else if (page?.data && Array.isArray(page.data)) {
        return total + page.data.length;
      } else if (Array.isArray(page)) {
        return total + page.length;
      }
      return total;
    }, 0);
  }, [apiData]);

  // Determine if we can load more events
  const canLoadMore = useMemo(() => {
    return hasNextPage && !isFetchingNextPage && currentRadius < maxRadius.current;
  }, [hasNextPage, isFetchingNextPage, currentRadius]);

  // Reset pagination and radius when userLocation changes
  useEffect(() => {
    remove();
    resetRadius();
  }, [userLocation]);

  return {
    data: apiData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    remove,
    currentRadius,
    expandRadius,
    resetRadius,
    isExpanding,
    eventCount,
    canLoadMore,
  };
};
